// Carried-items discovery for drag: items sitting on the dragged item's surface
// (and on THEIR surfaces, recursively) must move together with the drag.
import { readFurnitureData, type SceneNode, type SceneNodeChild } from './sceneNode';
import type { RenderFurniture } from './types';
import { getTileDims } from './tileMath';
import { type PlacementMode, toPlacementMode, isSurface } from './placement';
import {
  type SurfaceTileMap,
  isFullyOnSurface,
  getFurnitureDepthComponents,
  getOccupiedBaseTile,
} from './depthMath';

export interface CarriedItem {
  furnitureId: number;
  marker: SceneNode;
  furnitureData: RenderFurniture;
  offsetX: number;
  offsetY: number;
  origX: number;
  origY: number;
}

// Helper: build surface tile map for a single item at its current screen position.
// Mirror-aware: when the item's sprite is flipped (scale.x < 0), tile offsets swap
// axes (dx,dy) → (dy,dx), matching how computeSurfaceTileMap handles mirror.
export function buildSingleItemSurfaceMap(itemMarker: SceneNode, itemId: number): SurfaceTileMap {
  const singleMap: SurfaceTileMap = new Map();
  const sp = itemMarker.children[0] as SceneNodeChild | undefined;
  const fData = readFurnitureData(itemMarker, sp);
  const surfaceTilesArr = fData?.furniture_catalog?.surface_tiles;
  if (!surfaceTilesArr || surfaceTilesArr.length === 0) return singleMap;
  const { w, h, isRug } = getTileDims(fData!.furniture_catalog);
  const depth = getFurnitureDepthComponents(itemMarker, w, h, isRug);
  const cx = Math.round(depth.tileX);
  const cy = Math.round(depth.tileY);
  const isMir = sp ? sp.scale.x < 0 : false;
  const ownerPlacementMode: PlacementMode = toPlacementMode(fData?.placement_mode);
  for (const [dx, dy] of surfaceTilesArr) {
    const [tdx, tdy] = isMir ? [dy, dx] : [dx, dy];
    singleMap.set(`${cx + tdx},${cy + tdy}`, {
      ownerMarker: itemMarker,
      ownerZIndex: itemMarker.zIndex,
      ownerFurnitureId: itemId,
      ownerTileCount: surfaceTilesArr.length,
      ownerPlacementMode,
    });
  }
  return singleMap;
}

// Find all items that should move with the dragged item (items on its surface,
// items on THEIR surfaces, etc.), using a combined surface map.
//
// WHY not a simple BFS per-parent: an item can span multiple adjacent surfaces
// (e.g. a small table bridging two nightstands). Checking against only one parent's
// surface map would miss it. Instead we maintain a single growing combined surface
// map of every carried item found so far, and keep iterating until no new items
// are found (fixed-point). This naturally handles multi-surface spans and nesting.
export function collectCarriedItems(
  interiorMarkers: Map<number, SceneNode>,
  draggedFurnitureId: number,
  draggedMarker: SceneNode,
): { carriedItems: CarriedItem[]; carriedSet: Set<number> } {
  const carriedItems: CarriedItem[] = [];
  const carriedSet = new Set<number>([draggedFurnitureId]);
  // Seed the combined map with the dragged item's own surface tiles.
  const combinedCarriedSurfaceMap: SurfaceTileMap = new Map(
    buildSingleItemSurfaceMap(draggedMarker, draggedFurnitureId)
  );
  // Repeat until no new carried items are discovered in a full pass.
  let foundNew = true;
  while (foundNew) {
    foundNew = false;
    for (const [fId, otherMarker] of interiorMarkers.entries()) {
      if (carriedSet.has(fId)) continue;
      const csp = otherMarker.children[0] as SceneNodeChild | undefined;
      const cfData = readFurnitureData(otherMarker, csp);
      if (!cfData || !isSurface(toPlacementMode(cfData.placement_mode))) continue;
      const { w: cw, h: ch, isRug: cIsRug } = getTileDims(cfData.furniture_catalog);
      const depthComp = getFurnitureDepthComponents(otherMarker, cw, ch, cIsRug);
      const cx = Math.round(depthComp.tileX);
      const cy = Math.round(depthComp.tileY);
      const isMir = (csp?.scale?.x ?? 1) < 0;
      const base = getOccupiedBaseTile(depthComp, isMir);
      const fpTiles = cfData.furniture_catalog?.footprint_tiles;
      const tileCount = fpTiles?.length ?? (depthComp.effectiveWidthTiles * depthComp.effectiveHeightTiles);
      if (!isFullyOnSurface(fpTiles, cx, cy, base.rawBaseTileX, base.rawBaseTileY, base.overlayWidthTiles, base.overlayHeightTiles, combinedCarriedSurfaceMap, undefined, tileCount, isMir)) continue;
      carriedSet.add(fId);
      carriedItems.push({
        furnitureId: fId,
        marker: otherMarker,
        furnitureData: cfData,
        offsetX: otherMarker.position.x - draggedMarker.position.x,
        offsetY: otherMarker.position.y - draggedMarker.position.y,
        origX: otherMarker.position.x,
        origY: otherMarker.position.y,
      });
      // Add this item's surface tiles so items sitting on IT are found in the next pass.
      for (const [k, v] of buildSingleItemSurfaceMap(otherMarker, fId)) {
        combinedCarriedSurfaceMap.set(k, v);
      }
      foundNew = true;
    }
  }
  return { carriedItems, carriedSet };
}
